import type { AudienceSegmentEntry, InventoryBundle, InventoryDomain } from '@openx/types/gqlTypes.ts';
import type { AllTableCriteria } from '@openx/types/tableCriteria.ts';
import { useEffect, useMemo, useRef } from 'react';
import { useDealPrompt } from '../../hooks/useDealPrompt.ts';
import {
  type DealPromptFilters,
  dealPromptContextTypes,
  dealPromptFilterSegmentSearchMode,
} from '../../utils/types.ts';

export const useSelectAudienceItems = (
  items: InventoryDomain[] | InventoryBundle[] | AudienceSegmentEntry[],
  tableCriteria: AllTableCriteria<DealPromptFilters>,
  correctItemsSetter: (items: (InventoryDomain[] | InventoryBundle[]) & AudienceSegmentEntry[]) => void
) => {
  const {
    state: { contextType },
  } = useDealPrompt();

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const filtersRef = useRef(tableCriteria.filters);
  filtersRef.current = tableCriteria.filters;

  const correctItemsSetterRef = useRef(correctItemsSetter);
  correctItemsSetterRef.current = correctItemsSetter;

  const { segment_search_mode, account } = tableCriteria.filters ?? {};
  const accountFilterKey = useMemo(() => (account?.length ? [...account].sort().join(',') : ''), [account]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: use refs for latest items/filters/setter to avoid re-render loops
  useEffect(() => {
    if (contextType !== dealPromptContextTypes.AUDIENCE) return;

    const { segment_search_mode: mode, account: accountIds } = filtersRef.current ?? {};
    const audienceItems = itemsRef.current as AudienceSegmentEntry[];
    const hasSegmentFilter = mode === dealPromptFilterSegmentSearchMode.OXS_DATA_MARKETPLACE;
    const hasAccountFilter = Boolean(accountIds?.length);

    let matching = audienceItems;

    if (hasSegmentFilter) matching = matching.filter(item => item.sellable);
    if (hasAccountFilter) matching = matching.filter(item => accountIds!.includes(item.account_id));

    correctItemsSetterRef.current(matching as (InventoryDomain[] | InventoryBundle[]) & AudienceSegmentEntry[]);
  }, [contextType, segment_search_mode, accountFilterKey]);
};
